/**
 * What `wayai files pull` and `wayai files push` do with each path, decided three ways: the copy
 * the last pull recorded (`synced`), the local folder (`local`) and the base now (`remote`).
 * Compared by content hash — the value a write's `If-Match` is checked against.
 *
 * Push writes only what changed locally, and an update only onto the copy it was based on: a path
 * the base changed, deleted or gained since the pull is a conflict, never a write. Pull takes the
 * base's changes over unchanged local copies and keeps local changes, recorded at the version they
 * were based on, so the next push of one is refused rather than overwriting what the base gained.
 *
 * A push never gives the base a pair of paths no pull could write (`BaseNames`): two spellings of
 * one name (on a case-insensitive filesystem `leave.md` and `Leave.md` are one file), or one path
 * as both a file and a folder. Pull's side of that rule is read off the disk as it writes
 * (`diskClash`), not planned here.
 */

import { CaseIndex, remotePath, type LocalFile, type SyncedFile } from './local.js';

export type { LocalFile } from './local.js';

/** A file on the base, as its listing reports it. */
export interface RemoteFile {
  /** Path relative to the synced folder. */
  rel: string;
  version: number;
  content_sha256: string;
  content_type: string;
  size: number;
}

interface PlanInput {
  synced: Record<string, SyncedFile>;
  local: Map<string, LocalFile>;
  remote: Map<string, RemoteFile>;
}

/**
 * The base paths a create must not collide with, as full paths in the file type: one spelled
 * differently only in case at any level (`CaseIndex`), a file where the create needs a folder, or
 * a folder where it needs a file. Any of them is a pair no pull could write, so it refuses the
 * whole file type afterwards.
 */
export type Clash = 'case_twin' | 'file_folder';

class BaseNames {
  private readonly cases = new CaseIndex();
  private readonly files = new Set<string>();
  /** Each folder a path sits in, with one path in it to name. */
  private readonly folders = new Map<string, string>();

  constructor(paths: Iterable<string> = []) {
    for (const p of paths) this.add(p);
  }

  add(path: string): void {
    this.cases.add(path);
    this.files.add(path);
    const segments = path.split('/');
    for (let i = 1; i < segments.length; i++) {
      const folder = segments.slice(0, i).join('/');
      if (!this.folders.has(folder)) this.folders.set(folder, path);
    }
  }

  /** The path `path` would collide with, and how; undefined when it collides with none. */
  clash(path: string): { reason: Clash; other: string } | undefined {
    const twin = this.cases.twinOf(path);
    if (twin !== undefined) return { reason: 'case_twin', other: twin };
    const segments = path.split('/');
    for (let i = 1; i < segments.length; i++) {
      const folder = segments.slice(0, i).join('/');
      if (this.files.has(folder)) return { reason: 'file_folder', other: folder };
    }
    const inside = this.folders.get(path);
    return inside === undefined ? undefined : { reason: 'file_folder', other: inside };
  }
}

/** Whether the record already holds the base's copy exactly. */
function inStep(b: SyncedFile | undefined, r: RemoteFile): boolean {
  return b !== undefined && b.content_sha256 === r.content_sha256 && b.version === r.version;
}

function allPaths({ synced, local, remote }: PlanInput): string[] {
  return [...new Set([...Object.keys(synced), ...local.keys(), ...remote.keys()])].sort();
}

export type PushStep =
  /**
   * `after`: the deletes this create would otherwise clash with (a name spelled differently only in
   * case, or one path as both a file and a folder), and how — it may run only once they land.
   */
  | { op: 'create'; rel: string; local: LocalFile; after?: Array<{ rel: string; reason: Clash }> }
  | { op: 'update'; rel: string; local: LocalFile; remote: RemoteFile; ifMatch: string }
  | { op: 'delete'; rel: string; remote: RemoteFile; ifMatch: string };

export type PushConflict = 'changed_on_base' | 'deleted_on_base' | 'exists_on_base' | 'case_twin' | 'file_folder';

/**
 * A path left alone. `other` names what a `case_twin` or `file_folder` clash meets: the other
 * spelling, or the path that would be the file to this one's folder (or the reverse).
 */
export interface Conflict<R extends string> {
  rel: string;
  reason: R;
  other?: string;
}

export interface PushPlan {
  /** Deletes first, then creates and updates, each in path order: a case-only rename frees its name first. */
  steps: PushStep[];
  /** Identical on both sides already: recorded, nothing sent. */
  adopt: RemoteFile[];
  /** Gone from both sides: dropped from the record. */
  forget: string[];
  conflicts: Array<Conflict<PushConflict>>;
  /** Unchanged locally, changed on the base: left as is. */
  changedOnBase: string[];
  /** Unchanged locally, deleted on the base: left as is. */
  deletedOnBase: string[];
  /** On the base, never pulled: left as is (and never pruned). */
  onlyOnBase: string[];
  /** Deleted locally, kept on the base because the push was not asked to prune. */
  keptOnBase: string[];
}

/**
 * `folder` is the synced folder, and `elsewhere` every base path of the file type outside it — what
 * a folder-scoped listing cannot see, but a create must not collide with either (`BaseNames`).
 */
export function planPush(input: PlanInput & { prune: boolean; folder?: string; elsewhere?: string[] }): PushPlan {
  const plan: PushPlan = {
    steps: [],
    adopt: [],
    forget: [],
    conflicts: [],
    changedOnBase: [],
    deletedOnBase: [],
    onlyOnBase: [],
    keptOnBase: [],
  };
  const deletes: PushStep[] = [];
  const writes: PushStep[] = [];
  for (const rel of allPaths(input)) {
    const b = input.synced[rel];
    const l = input.local.get(rel);
    const r = input.remote.get(rel);

    if (l && r && l.content_sha256 === r.content_sha256) {
      if (!inStep(b, r)) plan.adopt.push(r);
      continue;
    }
    if (b) {
      if (l) {
        if (l.content_sha256 === b.content_sha256) {
          if (!r) plan.deletedOnBase.push(rel);
          else if (r.content_sha256 !== b.content_sha256) plan.changedOnBase.push(rel);
        } else if (!r) {
          plan.conflicts.push({ rel, reason: 'deleted_on_base' });
        } else if (r.content_sha256 !== b.content_sha256) {
          plan.conflicts.push({ rel, reason: 'changed_on_base' });
        } else {
          writes.push({ op: 'update', rel, local: l, remote: r, ifMatch: b.content_sha256 });
        }
      } else if (!r) {
        plan.forget.push(rel);
      } else if (!input.prune) {
        plan.keptOnBase.push(rel);
      } else if (r.content_sha256 !== b.content_sha256) {
        plan.conflicts.push({ rel, reason: 'changed_on_base' });
      } else {
        deletes.push({ op: 'delete', rel, remote: r, ifMatch: b.content_sha256 });
      }
    } else if (l) {
      if (r) plan.conflicts.push({ rel, reason: 'exists_on_base' });
      else writes.push({ op: 'create', rel, local: l });
    } else if (r) {
      plan.onlyOnBase.push(rel);
    }
  }

  // A create must not collide with a path the base keeps (`BaseNames`): no later pull could write
  // both. A path this push deletes first is out of the way — once that delete lands.
  const full = (rel: string) => remotePath(input.folder ?? '', rel);
  const deleted = new Set(deletes.map((d) => d.rel));
  const onBase = new BaseNames([
    ...[...input.remote.keys()].filter((rel) => !deleted.has(rel)).map(full),
    ...(input.elsewhere ?? []),
  ]);
  const freed = new BaseNames([...deleted].map(full));
  const kept: PushStep[] = [];
  for (const step of writes) {
    if (step.op === 'create') {
      const clash = onBase.clash(full(step.rel));
      if (clash !== undefined) {
        plan.conflicts.push({ rel: step.rel, ...clash });
        continue;
      }
      const after =
        freed.clash(full(step.rel)) === undefined
          ? []
          : [...deleted].flatMap((d) => {
              const held = new BaseNames([full(d)]).clash(full(step.rel));
              return held === undefined ? [] : [{ rel: d, reason: held.reason }];
            });
      kept.push(after.length === 0 ? step : { ...step, after });
    } else {
      kept.push(step);
    }
    onBase.add(full(step.rel));
  }
  plan.steps = [...deletes, ...kept];
  return plan;
}

export type PullKept = 'edited' | 'deleted';
export type PullConflict = 'both_changed' | 'deleted_on_base' | 'deleted_locally' | 'exists_locally';

export interface PullPlan {
  /** Base files to write locally — after `removes`, so a case-only rename frees its name first. */
  writes: Array<{ rel: string; remote: RemoteFile }>;
  /** Local files to remove: the base deleted them. Each leaves the record once it is removed. */
  removes: string[];
  /** Identical on both sides already: recorded, nothing written. */
  adopt: RemoteFile[];
  /** Gone from both sides: dropped from the record. */
  forget: string[];
  /** Local changes kept: the base has not moved past the version they were based on. */
  kept: Array<{ rel: string; reason: PullKept }>;
  /** Changed on both sides; the local copy is kept at the version it was based on. */
  conflicts: Array<Conflict<PullConflict>>;
  /** Paths whose local change `--overwrite` discards. */
  replacedLocal: string[];
}

export function planPull(input: PlanInput & { overwrite: boolean }): PullPlan {
  const plan: PullPlan = { writes: [], removes: [], adopt: [], forget: [], kept: [], conflicts: [], replacedLocal: [] };
  const { overwrite } = input;
  const take = (rel: string, r: RemoteFile, replacesLocal: boolean): void => {
    plan.writes.push({ rel, remote: r });
    if (replacesLocal) plan.replacedLocal.push(rel);
  };
  for (const rel of allPaths(input)) {
    const b = input.synced[rel];
    const l = input.local.get(rel);
    const r = input.remote.get(rel);

    if (r) {
      if (l && l.content_sha256 === r.content_sha256) {
        if (!inStep(b, r)) plan.adopt.push(r);
        continue;
      }
      const localUnchanged = b !== undefined && l !== undefined && l.content_sha256 === b.content_sha256;
      if (localUnchanged || (!b && !l)) {
        take(rel, r, false);
        continue;
      }
      // A local change: an edit, a deletion, or a file the last pull never saw.
      if (overwrite) {
        take(rel, r, true);
      } else if (!b) {
        plan.conflicts.push({ rel, reason: 'exists_locally' });
      } else if (r.content_sha256 === b.content_sha256) {
        plan.kept.push({ rel, reason: l ? 'edited' : 'deleted' });
      } else {
        plan.conflicts.push({ rel, reason: l ? 'both_changed' : 'deleted_locally' });
      }
    } else if (b) {
      if (!l) {
        plan.forget.push(rel);
      } else if (l.content_sha256 === b.content_sha256 || overwrite) {
        plan.removes.push(rel);
        if (l.content_sha256 !== b.content_sha256) plan.replacedLocal.push(rel);
      } else {
        // Still recorded, so a push of the edit is refused instead of re-creating the file.
        plan.conflicts.push({ rel, reason: 'deleted_on_base' });
      }
    }
  }

  return plan;
}
